'use client'

// utils 
import { cn } from "@/lib/utils"
import { useEffect, useLayoutEffect, useRef } from "react"

// components 
import { Textarea } from "@/components/ui/textarea"
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form"
import { SignUpCta } from "@/components/auth/sign-up-cta"
import { motion } from "motion/react"

// types 
import { UsePromptFormReturn } from "@/lib/types"

export function PromptAreaTextarea({
    promptForm,
}: {
    promptForm: UsePromptFormReturn,
}) {
    const {
        form,
        maxTextAreaLines,
        getPlaceholderForMode,
        onTextAreaFocus,
        onTextAreaBlur,
        handleKeyDown,
        userContextValue,
    } = promptForm

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const prompt = form.watch("prompt")

    // Auto-resize when prompt changes (e.g. loading from draft)
    useLayoutEffect(() => {
        const el = textareaRef.current
        if (!el) return
        if (!prompt) {
            el.style.height = 'auto'
            el.style.overflowY = 'hidden'
            return
        }
        el.style.height = 'auto'
        const scrollHeight = el.scrollHeight
        const maxHeight = maxTextAreaLines * 24
        const newHeight = Math.min(scrollHeight, maxHeight)
        el.style.height = `${newHeight}px`
        el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
    }, [prompt, maxTextAreaLines])

    // Focus on load without autofocus
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus()
        }
    }, [])

    return (
        <FormField
            control={form.control}
            name="prompt"
            render={({ field }) => (
                <FormItem className="w-full">
                    <FormControl>
                        <Textarea
                            {...field}
                            ref={textareaRef}
                            className={cn(
                                "w-full resize-none overflow-y-auto border-0 p-0 rounded-none shadow-none",
                                "focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none", // Remove focus ring and outline on select/click/focus
                            )}
                            placeholder={getPlaceholderForMode()}
                            onInput={(e) => {
                                const target = e.target as HTMLTextAreaElement
                                target.style.height = 'auto'
                                const scrollHeight = target.scrollHeight
                                const maxHeight = maxTextAreaLines * 24 // approximate line height
                                const newHeight = Math.min(scrollHeight, maxHeight)
                                target.style.height = newHeight + 'px'
                                target.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
                            }}
                            onKeyDown={handleKeyDown}
                            onFocus={onTextAreaFocus}
                            onBlur={() => {
                                onTextAreaBlur()
                                field.onBlur()
                            }}
                        />
                    </FormControl>
                    <FormMessage />
                    {/* Inline CTA for unauthenticated max-length error */}
                    {form.formState.errors.prompt?.message?.toString().includes('10,000') && (
                        <div className="text-xs text-muted-foreground mt-1">
                            Want to write longer messages? <SignUpCta variant="inline" />
                        </div>
                    )}
                </FormItem>
            )}
        />
    )
}